/**
 * prepaidKeyManager.ts
 *
 * Centralized manager for prepaid API key storage.
 *
 * SECURITY CONTEXT:
 * Previously, full API key values and metadata were stored in plaintext
 * localStorage, exposing them to any JavaScript running on the page (XSS,
 * malicious browser extensions, third-party scripts). This module applies
 * several defense-in-depth mitigations:
 *
 *   1. sessionStorage instead of localStorage -- keys are cleared when the
 *      browser tab/window is closed, limiting the persistence window.
 *
 *   2. Truncated display keys -- the UI only needs a prefix/suffix for
 *      display. We store a truncated identifier separately so most reads
 *      never touch the full key.
 *
 *   3. Obfuscation layer -- full key values are base64-encoded and reversed
 *      before storage. This is NOT encryption (the "key" to decode is in
 *      this very file), but it prevents trivial harvesting by scripts that
 *      simply read sessionStorage values as plaintext strings or scan for
 *      API-key-shaped patterns.
 *
 *   4. Custom event bus -- same-tab reactivity via "prepaidKeysUpdated"
 *      custom events, consistent with the existing pattern.
 *
 * IDEAL FUTURE STATE (backend changes required):
 * The correct long-term fix is to never store full API keys client-side at
 * all. The backend should:
 *   - Store prepaid keys server-side, associated with a wallet address or
 *     session.
 *   - Expose a GET /v3/prepaid/list endpoint that returns keys with
 *     truncated values (e.g., "abc1...xyz9") plus metadata (credits, usage,
 *     timestamps).
 *   - Return the full key ONLY once at creation time (shown in the
 *     PrepaidKeyCreatedModal, which already does this).
 *   - Accept the full key for refill/delete operations via the existing
 *     POST endpoints, where the user pastes or selects the key they saved.
 *
 * With that architecture, this module would fetch from the API instead of
 * reading sessionStorage, and the full key would never be persisted in the
 * browser beyond the initial creation response.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface PrepaidKey {
  key: string;          // full API key (obfuscated in storage)
  creditsUsed: number;
  creditsAvailable: number;
  lastUsed: string;     // ISO date string or ""
  createdAt: string;    // ISO date string
}

/** The shape we actually persist -- display-only truncated key + obfuscated full key. */
interface StoredPrepaidKey {
  /** First 8 + last 4 characters of the key, for display only. */
  displayKey: string;
  /** Full key, obfuscated (base64 + reversed). NOT encryption. */
  obfuscatedKey: string;
  creditsUsed: number;
  creditsAvailable: number;
  lastUsed: string;
  createdAt: string;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STORAGE_KEY = "prepaidKeys_v2";

// ---------------------------------------------------------------------------
// Obfuscation helpers (defense-in-depth, NOT cryptographic security)
// ---------------------------------------------------------------------------

/**
 * Obfuscate a string so it is not stored as recognizable plaintext.
 *
 * IMPORTANT: This is a trivial reversible transform (base64 + string
 * reversal). It stops automated scrapers that look for raw API key
 * patterns in storage, but any attacker who reads this source can
 * reverse it instantly. True protection requires server-side storage.
 */
function obfuscate(value: string): string {
  try {
    const binary =
      typeof TextEncoder !== "undefined"
        ? String.fromCharCode(...Array.from(new TextEncoder().encode(value)))
        : encodeURIComponent(value).replace(
          /%([0-9A-F]{2})/g,
          (_, byte) => String.fromCharCode(parseInt(byte, 16)),
        );
    const b64 = btoa(binary);
    return b64.split("").reverse().join("");
  } catch {
    // Fallback: still reverse to avoid plaintext, even if btoa fails
    return value.split("").reverse().join("");
  }
}

/** Reverse the obfuscation applied by `obfuscate()`. */
function deobfuscate(value: string): string {
  try {
    const b64 = value.split("").reverse().join("");
    const binary = atob(b64);
    if (typeof TextDecoder !== "undefined") {
      const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
      return new TextDecoder().decode(bytes);
    }
    const percentEncoded = Array.from(binary)
      .map(
        (char) => `%${char.charCodeAt(0).toString(16).toUpperCase().padStart(2, "0")}`,
      )
      .join("");
    return decodeURIComponent(percentEncoded);
  } catch {
    // Fallback: assume plain reversal
    return value.split("").reverse().join("");
  }
}

/** Build a truncated display string: first 8 chars + "..." + last 4 chars. */
function truncateKey(key: string): string {
  if (key.length <= 14) return key; // too short to truncate meaningfully
  return `${key.slice(0, 8)}...${key.slice(-4)}`;
}

// ---------------------------------------------------------------------------
// Internal storage I/O
// ---------------------------------------------------------------------------

function readStorage(): StoredPrepaidKey[] {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    return JSON.parse(raw) as StoredPrepaidKey[];
  } catch {
    return [];
  }
}

function writeStorage(keys: StoredPrepaidKey[]): void {
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(keys));
  // Notify same-tab listeners (storage event only fires cross-tab).
  window.dispatchEvent(new Event("prepaidKeysUpdated"));
}

// ---------------------------------------------------------------------------
// Migration: move legacy plaintext localStorage data to sessionStorage
// ---------------------------------------------------------------------------

const LEGACY_STORAGE_KEY = "prepaidKeys";

/**
 * One-time migration that runs on first access. If the old plaintext
 * localStorage entry exists, we migrate it to the new format in
 * sessionStorage and then DELETE the insecure localStorage entry.
 */
function migrateLegacyData(): void {
  try {
    const legacy = localStorage.getItem(LEGACY_STORAGE_KEY);
    if (!legacy) return;

    const legacyKeys: PrepaidKey[] = JSON.parse(legacy);
    if (!Array.isArray(legacyKeys) || legacyKeys.length === 0) {
      localStorage.removeItem(LEGACY_STORAGE_KEY);
      return;
    }

    // Only migrate if we don't already have data in the new store
    const existing = readStorage();
    if (existing.length === 0) {
      const migrated: StoredPrepaidKey[] = legacyKeys.map((k) => ({
        displayKey: truncateKey(k.key),
        obfuscatedKey: obfuscate(k.key),
        creditsUsed: k.creditsUsed,
        creditsAvailable: k.creditsAvailable,
        lastUsed: k.lastUsed,
        createdAt: k.createdAt,
      }));
      writeStorage(migrated);
    }

    // Always remove the insecure plaintext localStorage entry
    localStorage.removeItem(LEGACY_STORAGE_KEY);
  } catch {
    // Best-effort; don't break the app if migration fails
    try {
      localStorage.removeItem(LEGACY_STORAGE_KEY);
    } catch { /* ignore */ }
  }
}

// Run migration immediately on module load
migrateLegacyData();

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Retrieve all stored prepaid keys with their full (deobfuscated) values.
 * The full key is needed for refill/delete API calls and for the table's
 * key column (which already truncates for display).
 */
export function getPrepaidKeys(): PrepaidKey[] {
  migrateLegacyData(); // ensure migration on every read (idempotent)
  return readStorage().map((stored) => ({
    key: deobfuscate(stored.obfuscatedKey),
    creditsUsed: stored.creditsUsed,
    creditsAvailable: stored.creditsAvailable,
    lastUsed: stored.lastUsed,
    createdAt: stored.createdAt,
  }));
}

/**
 * Get only the truncated display identifiers and metadata -- use this
 * when you don't need the full key value (e.g., rendering the table).
 */
export function getPrepaidKeysForDisplay(): Array<
  Omit<PrepaidKey, "key"> & { displayKey: string }
> {
  migrateLegacyData();
  return readStorage().map((stored) => ({
    displayKey: stored.displayKey,
    creditsUsed: stored.creditsUsed,
    creditsAvailable: stored.creditsAvailable,
    lastUsed: stored.lastUsed,
    createdAt: stored.createdAt,
  }));
}

/** Persist a newly created prepaid key. */
export function savePrepaidKey(key: PrepaidKey): void {
  const stored = readStorage();
  stored.push({
    displayKey: truncateKey(key.key),
    obfuscatedKey: obfuscate(key.key),
    creditsUsed: key.creditsUsed,
    creditsAvailable: key.creditsAvailable,
    lastUsed: key.lastUsed,
    createdAt: key.createdAt,
  });
  writeStorage(stored);
}

/** Remove a prepaid key by its full key value. */
export function removePrepaidKey(fullKey: string): void {
  const stored = readStorage().filter(
    (s) => deobfuscate(s.obfuscatedKey) !== fullKey
  );
  writeStorage(stored);
}

/** Add credits to an existing prepaid key (identified by full key value). */
export function updatePrepaidKeyCredits(
  fullKey: string,
  addedCredits: number
): void {
  const stored = readStorage().map((s) =>
    deobfuscate(s.obfuscatedKey) === fullKey
      ? { ...s, creditsAvailable: s.creditsAvailable + addedCredits }
      : s
  );
  writeStorage(stored);
}

/** Check whether any prepaid keys are stored. */
export function hasPrepaidKeys(): boolean {
  migrateLegacyData();
  return readStorage().length > 0;
}
